(() => {
  'use strict';

  const CLIENT_PAGE_SIZE = 25;

  const normalize = (value) => String(value || '').trim().toLocaleLowerCase('nl');

  function compactPages(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

    const values = new Set([1, total, current - 1, current, current + 1]);
    const pages = [...values].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);
    const output = [];

    pages.forEach((page, index) => {
      if (index > 0 && page - pages[index - 1] > 1) output.push('ellipsis');
      output.push(page);
    });

    return output;
  }

  async function fetchSourcePage(root, page, sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error(`Bronpagina ${page} kon niet worden geladen (${response.status})`);

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const mode = root.dataset.pwMode || '';
    const fetchedRoot = documentFragment.querySelector(`[data-pw-library][data-pw-mode="${mode}"]`) ||
      documentFragment.querySelector('[data-pw-library]');

    if (!fetchedRoot) throw new Error(`Bronpagina ${page} bevat geen bibliotheeksectie`);

    return {
      page,
      rows: [...fetchedRoot.querySelectorAll('[data-pw-row]')]
    };
  }

  async function initLibrary(root) {
    if (!(root instanceof HTMLElement) || root.dataset.pwReady === 'true') return;
    root.dataset.pwReady = 'true';

    const input = root.querySelector('[data-pw-search]');
    const category = root.querySelector('[data-pw-category]');
    const status = root.querySelector('[data-pw-status]');
    const submit = root.querySelector('[data-pw-submit]');
    const count = root.querySelector('[data-pw-count]');
    const empty = root.querySelector('[data-pw-empty]');
    const pagination = root.querySelector('[data-pw-pagination]');
    const list = root.querySelector('.pw-k__list');
    const sourceMeta = root.querySelector('[data-pw-source-meta]');
    const loading = root.querySelector('[data-pw-loading]');

    if (!input || !list) return;

    const totalSourcePages = Math.max(1, Number(sourceMeta?.dataset.pages || 1));
    const currentSourcePage = Math.max(1, Number(sourceMeta?.dataset.currentPage || 1));
    const sectionId = sourceMeta?.dataset.sectionId || '';

    if (totalSourcePages > 1 && sectionId) {
      if (loading) {
        loading.hidden = false;
        loading.textContent = 'Alle producten laden…';
      }

      const pagesToLoad = Array.from({ length: totalSourcePages }, (_, index) => index + 1)
        .filter((page) => page !== currentSourcePage);

      const results = await Promise.allSettled(
        pagesToLoad.map((page) => fetchSourcePage(root, page, sectionId))
      );

      const seen = new Set(
        [...list.querySelectorAll('[data-pw-row]')].map((row) => row.dataset.productId || row.dataset.search)
      );
      let failedPages = 0;

      results.forEach((result) => {
        if (result.status !== 'fulfilled') {
          failedPages += 1;
          console.warn('[PW bibliotheek]', result.reason);
          return;
        }

        result.value.rows.forEach((row) => {
          const key = row.dataset.productId || row.dataset.search;
          if (!key || seen.has(key)) return;
          seen.add(key);
          list.appendChild(document.importNode(row, true));
        });
      });

      if (loading) {
        if (failedPages > 0) {
          loading.hidden = false;
          loading.textContent = 'Een deel van de resultaten kon niet worden geladen.';
        } else {
          loading.hidden = true;
        }
      }
    }

    let rows = [...list.querySelectorAll('[data-pw-row]')];
    let currentPage = 1;

    if (category) {
      category.querySelectorAll('[data-pw-generated-option]').forEach((option) => option.remove());
      const categories = [...new Set(rows.map((row) => row.dataset.category).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'nl'));

      categories.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.dataset.pwGeneratedOption = 'true';
        option.textContent = value.replace(/(^|\s)\S/g, (character) => character.toUpperCase());
        category.appendChild(option);
      });
    }

    const params = new URLSearchParams(window.location.search);
    const preset = params.get('q') || params.get('zoek');
    if (preset) input.value = preset;

    function filteredRows() {
      const query = normalize(input.value);
      const selectedCategory = normalize(category?.value);
      const selectedStatus = normalize(status?.value);

      return rows.filter((row) => {
        const searchText = normalize(row.dataset.search);
        const rowCategory = normalize(row.dataset.category);
        const rowStatus = normalize(row.dataset.status);

        return (!query || searchText.includes(query)) &&
          (!selectedCategory || rowCategory === selectedCategory) &&
          (!selectedStatus || rowStatus === selectedStatus);
      });
    }

    function renderPagination(totalPages) {
      if (!pagination) return;
      pagination.innerHTML = '';
      pagination.hidden = totalPages <= 1;
      if (totalPages <= 1) return;

      const createButton = (label, page, options = {}) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.disabled = Boolean(options.disabled);
        button.setAttribute('aria-label', options.ariaLabel || `Pagina ${page}`);
        if (options.current) button.setAttribute('aria-current', 'page');
        button.addEventListener('click', () => {
          if (button.disabled || currentPage === page) return;
          currentPage = page;
          render();
          root.querySelector('.pw-k__tools')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return button;
      };

      pagination.appendChild(createButton('‹', currentPage - 1, {
        disabled: currentPage === 1,
        ariaLabel: 'Vorige pagina'
      }));

      compactPages(currentPage, totalPages).forEach((value) => {
        if (value === 'ellipsis') {
          const ellipsis = document.createElement('span');
          ellipsis.textContent = '…';
          ellipsis.setAttribute('aria-hidden', 'true');
          pagination.appendChild(ellipsis);
          return;
        }

        pagination.appendChild(createButton(String(value), value, {
          current: value === currentPage
        }));
      });

      pagination.appendChild(createButton('›', currentPage + 1, {
        disabled: currentPage === totalPages,
        ariaLabel: 'Volgende pagina'
      }));
    }

    function render() {
      const matches = filteredRows();
      const totalPages = Math.max(1, Math.ceil(matches.length / CLIENT_PAGE_SIZE));
      currentPage = Math.min(currentPage, totalPages);
      const start = (currentPage - 1) * CLIENT_PAGE_SIZE;
      const visibleRows = new Set(matches.slice(start, start + CLIENT_PAGE_SIZE));

      rows.forEach((row) => {
        row.hidden = !visibleRows.has(row);
      });

      if (count) count.textContent = String(matches.length);
      if (empty) empty.hidden = matches.length > 0;
      renderPagination(matches.length > 0 ? totalPages : 0);
    }

    const resetAndRender = () => {
      currentPage = 1;
      render();
    };

    input.addEventListener('input', resetAndRender);
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      resetAndRender();
    });
    category?.addEventListener('change', resetAndRender);
    status?.addEventListener('change', resetAndRender);
    submit?.addEventListener('click', resetAndRender);

    rows = [...list.querySelectorAll('[data-pw-row]')];
    render();
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-pw-library]').forEach((root) => {
      initLibrary(root).catch((error) => {
        console.warn('[PW bibliotheek] Initialiseren mislukt.', error);
        const loading = root.querySelector('[data-pw-loading]');
        if (loading) {
          loading.hidden = false;
          loading.textContent = 'De volledige lijst kon niet worden geladen.';
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
